import type { JobDetails } from '@seek/ca-graphql-schema/types';
import type { Country, ShareType, SortMode } from '@seek/chalice-types';
import * as tealiumAdapter from '@seek/tealium-adapter';

const { adapter: createTealiumAdapter, getScriptTag: getTealiumTag } =
  tealiumAdapter;

import type {
  ContactMatchPayload,
  DataKey,
  DataLayer,
  Experiment,
  HubbleExperiments,
  JobMailPosition,
  SaveSearchPosition,
} from './DataLayer';
import createStore from './createStore';
import {
  type PageTitle,
  mapCurrentPageTitle,
} from './mappers/currentPageTitle';
import { mapEventName } from './mappers/eventName';
import {
  type JobActionOrigin,
  getJobActionOrigin,
} from './mappers/getJobActionOrigin';
import { mapJobTagsToBadges } from './mappers/jobBadges';
import { getJobViewOrigin } from './mappers/jobViewOrigin';
import { mapJob } from './mappers/jobdetails';
import {
  type LinkContext,
  type LinkPayload,
  type NavigationPressedProps,
  type PageName,
  mapLinkContext,
  mapLinkType,
} from './mappers/link';
import { mapNudgeProps } from './mappers/mapNudgeProps';
import { nullify } from './mappers/nullify';
import { pathToPageTitle } from './mappers/pathToPageTitle';
import {
  type Search,
  evaluateRadialFilterValues,
  isRefinement,
  mapSearch,
  mapSearchSalaryRange,
} from './mappers/search';
import { mapSiteCountry } from './mappers/siteCountry';
import { type UserDetails, mapUserDetails } from './mappers/user';
import type {
  Analytics,
  DisplayAdViewedProps,
  HomepageMarketingBannerClickedProps,
  JobCardAccordionClickedPayload,
  JobDetailsProps,
  JobViewType,
  LmisTypePayload,
  LoggedOutRecsImpressionPayload,
  RecentSearchClickPayload,
  RecentSearchesImpressionPayload,
  RelatedSearchPressedProps,
  SignInRegisterPressedProps,
  VerifiedHirerBadgePayload,
} from './types';
import { mockTealiumAdapter } from './types/mockTealiumAdapter';
import { searchOrigins } from './utils/constants';
import evaluateLotameValues from './utils/evaluateLotameValues';
import { getEnv } from './utils/getEnv';
import hashToUserQueryId from './utils/hashToUserQueryId';

interface Options {
  environment: string;
  profile?: 'candidate-main';
  useMock?: boolean;
}

const defaultifyLoggedInState = (state: DataLayer): DataLayer => {
  const { isLoggedIn, loginId } = state;
  return {
    ...state,
    isLoggedIn:
      typeof isLoggedIn !== 'undefined' ? (isLoggedIn ?? false) : undefined,
    loginId: typeof loginId !== 'undefined' ? nullify(loginId) : undefined,
  };
};

const createAnalytics = (
  options: Options,
  initialDataLayer: DataLayer = {},
): Analytics => {
  const {
    environment,
    useMock = false,
    profile = 'candidate-main',
  } = options || {};

  const persistentKeys: DataKey[] = [
    'brand',
    'isLoggedIn',
    'isSortModeChange',
    'loginId',
    'previousPage',
    'previousSearchId',
    'profileGuid',
    'siteCountry',
    'siteLanguage',
    'siteSection',
    'solVisitorId',
    'searchKeywordSelected',
    'sortedBy',
    'zone',
    'experiments',
    'hubbleExperiments',
    'experimentsEligible',
  ];

  const {
    update: updateDataLayer,
    getState: getDataLayer,
    prune: pruneDataLayer,
  } = createStore(defaultifyLoggedInState(initialDataLayer), persistentKeys);

  const tealium: tealiumAdapter.Adapter = useMock
    ? mockTealiumAdapter
    : createTealiumAdapter();

  const searchPerformed = (props: Search) => {
    updateDataLayer(mapSearch({ dataLayer: getDataLayer(), search: props }));
  };

  const sortModeChanged = (sortMode: SortMode) => {
    updateDataLayer({
      isSortModeChange: true,
      sortedBy: sortMode,
    });
  };

  const keywordChanged = (
    text: string,
    isSuggestion: boolean,
    prevText: string,
    keywordListCount?: number,
    keywordRank?: number,
    suggestionMetadata?: {
      requestToken?: string;
      flightId?: string;
    },
  ) => {
    if (isSuggestion) {
      updateDataLayer({
        searchKeywordSelected: text,
      });

      tealium.link('keyword_autosuggest_pressed', {
        ...getDataLayer(),
        searchKeywordTyped: prevText,
        searchKeywordSelected: text,
        linkName: 'keyword_autosuggest_pressed',
        searchKeywordRank: keywordRank,
        keywordListCount,
        experimentFlightId: suggestionMetadata?.flightId,
      });
    }
  };

  const userDetailsUpdated = (props: UserDetails) => {
    updateDataLayer(mapUserDetails(props));
  };

  let isFirstLocationChange = true;
  const locationChanged: Analytics['locationChanged'] = ({ pathname }) => {
    const currentPage = pathToPageTitle(pathname);
    const { currentPage: previousPage, searchId: previousSearchId } =
      getDataLayer();

    updateDataLayer({
      ...(currentPage ? { currentPage } : {}),
      ...(previousPage ? { previousPage } : {}),
      ...(previousSearchId ? { previousSearchId } : {}),
    });

    if (!isFirstLocationChange) {
      pruneDataLayer();
    }
    isFirstLocationChange = false;
  };

  const countryChanged = (country: Country) => {
    updateDataLayer(mapSiteCountry(country));
  };

  const homePageLoaded = (homePageOptions: { flightId?: string }) => {
    const { flightId } = homePageOptions || {};
    updateDataLayer({
      currentPage: 'home',
      experimentFlightId: flightId,
    });
    tealium.view('home_displayed', getDataLayer());
  };

  const feedbackSurveyViewed = ({
    currentPageSection,
    product,
  }: {
    currentPageSection: string;
    product: string;
  }) => {
    tealium.link('feedback_survey_displayed', {
      ...getDataLayer(),
      currentPageSection,
      product,
    });
  };

  const feedbackSurveyPressed = ({
    currentPageSection,
    product,
    elementText,
    elementAction,
  }: {
    currentPageSection: string;
    product: string;
    elementText: 'Positive' | 'Negative';
    elementAction: string;
  }) => {
    tealium.link('feedback_survey_pressed', {
      ...getDataLayer(),
      currentPageSection,
      product,
      elementText,
      elementAction,
    });
  };

  const loggedOutRecsImpression = ({
    cardDisplayedCount,
  }: LoggedOutRecsImpressionPayload) => {
    tealium.link('logged_out_recs_viewed', {
      ...getDataLayer(),
      cardDisplayedCount: cardDisplayedCount ?? nullify(cardDisplayedCount),
    });
  };

  const searchResultsPageLoaded: Analytics['searchResultsPageLoaded'] = ({
    flightId,
    trackingCorrelationId,
    linkPosition,
    jobBadgesFilter = [],
    searchResultsTab,
  }) => {
    const { relatedSearchPosition, searchOrigin, searchSalaryRange } =
      getDataLayer();

    updateDataLayer({
      ...mapCurrentPageTitle('search results'),
      ...mapSearchSalaryRange({ searchSalaryRange }),
      experimentFlightId: flightId,
      relatedSearchPosition: nullify(relatedSearchPosition),
      searchOrigin: searchOrigin ?? searchOrigins.OTHERS,
    });

    tealium.view('search_results_displayed', {
      ...getDataLayer(),
      ...evaluateLotameValues(getDataLayer()),
      ...evaluateRadialFilterValues(getDataLayer()),
      isRefinement: isRefinement(getDataLayer()),
      trackingCorrelationId,
      linkPosition,
      jobBadgesFilter: jobBadgesFilter.length > 0 ? jobBadgesFilter : undefined,
      searchResultsTab,
    });

    // reset sort mode change
    updateDataLayer({}, { omitKeys: ['isSortModeChange', 'sortedBy'] });
  };

  const globalLandingPageLoaded = () => {
    const eventName = 'global_landing_page_displayed';
    tealium.view(eventName, {
      ...getDataLayer(),
      eventName,
    });
  };

  const recentSearchClicked = ({
    cardDisplayedCount,
    cardPosition,
    isExitLink,
  }: RecentSearchClickPayload) => {
    tealium.link('last_search_recent_searches_card_pressed', {
      ...getDataLayer(),
      cardDisplayedCount,
      cardType: 'Keyword',
      cardPosition,
      isExitLink,
      linkName: 'last_search_recent_searches_card_pressed',
    });

    updateDataLayer(
      {
        searchOrigin: searchOrigins.RECENT_SEARCH,
      },
      { keepKeysForNextPrune: ['searchOrigin'] },
    );
  };

  const recentSearchesImpression = ({
    cardDisplayedCount,
    isExitLink,
    recentSearchesCollapsed = false,
  }: RecentSearchesImpressionPayload) => {
    tealium.link('last_search_recent_searches_card_viewed', {
      ...getDataLayer(),
      cardDisplayedCount,
      cardType: 'Keyword',
      isExitLink,
      linkName: 'last_search_recent_searches_card_viewed',
      recentSearchesCollapsed,
    });
  };

  const lastSearchClicked = () => {
    tealium.link('last-search-clicked', {
      ...getDataLayer(),
      linkName: 'last-search',
    });
  };

  const createSaveSearchClicked = (props: {
    saveSearchPosition: SaveSearchPosition;
  }) => {
    const { saveSearchPosition } = props || {};
    updateDataLayer({ saveSearchPosition });

    tealium.link('search_save_pressed', {
      ...getDataLayer(),
      linkName: 'search_save_pressed',
    });
  };

  const createSaveSearchError = ({ message }: { message: string }) => {
    tealium.link('alert_viewed', {
      ...getDataLayer(),
      linkName: 'alert_viewed',
      alertShown: [
        {
          type: 'error',
          message,
        },
      ],
    });
  };

  const createUnregisteredSaveSearchClicked = ({
    jobMailPosition,
    linkText,
  }: {
    jobMailPosition: JobMailPosition;
    linkText: string;
  }) => {
    tealium.link('search_save_pressed', {
      ...getDataLayer(),
      linkName: 'job-mail',
      jobMailPosition,
      linkText,
    });
  };

  const createUnregisteredSaveSearchError = ({
    message,
  }: {
    message: string;
  }) => {
    tealium.link('alert_viewed', {
      ...getDataLayer(),
      linkName: 'alert_viewed',
      alertShown: [
        {
          type: 'error',
          message,
        },
      ],
    });
  };

  const jobCardAccordionLinkClicked: Analytics['jobCardAccordionLinkClicked'] =
    ({
      jobId,
      jobListingPosition,
      linkAction,
    }: JobCardAccordionClickedPayload) => {
      const eventName = 'accordion_pressed';
      const linkSection = 'job card';
      tealium.link(eventName, {
        ...getDataLayer(),
        jobId,
        jobListingPosition,
        linkAction,
        linkSection,
      });
    };

  const jobDetailsFetched = (job: JobDetails) => {
    updateDataLayer(mapJob(job), {
      omitKeys: ['currentPage', 'previousPage'],
    });
  };

  const getCurrentPageFromJobViewType = (
    jobViewType: JobDetailsProps['jobViewType'],
  ): PageTitle => {
    switch (jobViewType) {
      case 'split':
        return 'search results';
      case 'drawer':
        return 'home';
      default:
        return 'job details';
    }
  };

  const jobDetailsPageLoaded = ({
    brand,
    isAddSalaryPrefNudgeShown,
    isFeatured,
    isSalaryMatch,
    jobBadges,
    jobCardSellPointCount,
    jobListingType,
    jobListingPosition,
    jobViewOriginQuery,
    jobActionOrigin,
    jobViewType,
    searchRemoteOptions,
    flightId,
    trackingCorrelationId,
    isApplied,
    isHirerVerified,
    matchedQualitiesRequestToken,
    isMatchedSkillsNudgeShown = false,
    jobBadgesFilter = [],
    hasSourcr = false,
    jobBadgesShown = [],
    jobApplicantsVolume,
  }: JobDetailsProps) => {
    const eventName = 'job_details_displayed';
    updateDataLayer({
      brand,
      currentPage: getCurrentPageFromJobViewType(jobViewType),
      experimentFlightId: flightId,
      isAddSalaryPrefNudgeShown,
      isApplied,
      isFeatured,
      isSalaryMatch,
      searchRemoteOptions,
      jobBadges,
      jobListingType,
      jobViewType,
      ...(typeof jobListingPosition !== undefined
        ? { jobListingPosition: jobListingPosition?.toString() }
        : {}),
    });

    const trackingObject = {
      ...getDataLayer(),
      ...getJobViewOrigin(jobViewOriginQuery),
      ...getJobActionOrigin(jobActionOrigin),
      hasSourcr,
      isHirerVerified,
      isMatchedSkillsNudgeShown,
      jobBadgesFilter: jobBadgesFilter.length > 0 ? jobBadgesFilter : undefined,
      jobCardSellPointCount,
      matchedQualitiesRequestToken,
      trackingCorrelationId,
      jobBadgesShown,
      jobApplicantsVolume,
    };

    return tealium.view(eventName, trackingObject);
  };

  const verifiedHirerBadgeClicked: Analytics['verifiedHirerBadgeClicked'] = ({
    jobId,
    jobListingPosition,
    linkSection,
  }: VerifiedHirerBadgePayload) => {
    const eventName = 'content_element_pressed';
    const linkText = 'Verified Hirer Badge';
    tealium.link(eventName, {
      ...getDataLayer(),
      jobId,
      jobListingPosition,
      linkText,
      linkSection,
    });
  };

  const jobApplyClicked: Analytics['jobApplyClicked'] = ({
    experimentFlightId,
    href,
    jobBadges,
    jobViewOriginQuery,
    trackingCorrelationId,
    isHirerVerified,
    isMatchedSkillsWidgetShown = false,
    isMatchedSkillsNudgeShown = false,
    jobCardSellPointCount,
    jobBadgesFilter = [],
    hasSourcr = false,
    jobBadgesShown = [],
    jobApplicantsVolume,
  }) => {
    const mappedEventName = 'job_apply_pressed';
    if (experimentFlightId) {
      updateDataLayer({
        experimentFlightId,
      });
    }

    tealium.link(mappedEventName, {
      ...getDataLayer(),
      ...mapLinkType({
        linkName: mappedEventName,
        href,
      }),
      ...getJobViewOrigin(jobViewOriginQuery),
      isHirerVerified,
      isMatchedSkillsWidgetShown,
      isMatchedSkillsNudgeShown,
      jobBadges,
      jobBadgesFilter: jobBadgesFilter.length > 0 ? jobBadgesFilter : undefined,
      jobCardSellPointCount,
      trackingCorrelationId,
      hasSourcr,
      jobBadgesShown,
      jobApplicantsVolume,
    });
  };

  const saveJobClicked: Analytics['saveJobClicked'] = ({
    jobId,
    linkPosition,
    jobViewOriginQuery,
  }) => {
    tealium.link('job_save_pressed', {
      ...getDataLayer(),
      jobId,
      linkPosition,
      ...getJobViewOrigin(jobViewOriginQuery),
    });
  };

  const savedJobCreated: Analytics['savedJobCreated'] = ({
    jobId,
    jobListingPosition,
    jobViewOriginQuery,
    jobViewType,
    linkPosition,
    trackingCorrelationId,
    currentPageSection,
  }) => {
    tealium.link('job_save_succeeded', {
      ...getDataLayer(),
      currentPageSection,
      jobId,
      jobListingPosition,
      ...getJobViewOrigin(jobViewOriginQuery),
      jobViewType,
      linkPosition,
      trackingCorrelationId,
    });
  };

  const searchHelpPressed = () => {
    tealium.link('search_help_pressed', getDataLayer());
  };

  const nudgeDismissed: Analytics['nudgeDismissed'] = (args) => {
    tealium.link('nudge_dismiss_pressed', {
      ...getDataLayer(),
      ...mapNudgeProps(args),
    });
  };

  const nudgePressed: Analytics['nudgePressed'] = ({
    linkText,
    href,
    ...rest
  }) => {
    tealium.link('nudge_pressed', {
      ...getDataLayer(),
      ...mapNudgeProps(rest),
      linkText,
      ...(href ? { isExitLink: false } : {}),
    });
  };

  const nudgeViewed: Analytics['nudgeViewed'] = (args) => {
    tealium.link('nudge_viewed', {
      ...getDataLayer(),
      ...mapNudgeProps(args),
    });
  };

  const savedJobRemoved: Analytics['savedJobRemoved'] = ({
    currentPageSection,
    jobId,
    jobListingPosition,
    jobViewOriginQuery,
    jobViewType,
    linkPosition,
    trackingCorrelationId,
  }) => {
    tealium.link('job_unsave_succeeded', {
      ...getDataLayer(),
      currentPageSection,
      jobId,
      jobListingPosition,
      ...getJobViewOrigin(jobViewOriginQuery),
      jobViewType,
      linkPosition,
      trackingCorrelationId,
    });
  };

  const signInPressed = ({
    linkContext: { linkPosition, linkText },
    actionOrigin,
  }: SignInRegisterPressedProps) => {
    tealium.link('login_pressed', {
      ...getDataLayer(),
      isExitLink: false,
      linkName: `${linkPosition}:sign-in`,
      linkPosition,
      ...(actionOrigin ? { actionOrigin } : {}),
      ...(linkText ? { linkText } : {}),
    });
  };

  const registerPressed = ({
    linkContext: { linkPosition, linkText },
    actionOrigin,
  }: SignInRegisterPressedProps) => {
    tealium.link('register_pressed', {
      ...getDataLayer(),
      isExitLink: false,
      linkName: `${linkPosition}:register`,
      linkPosition,
      ...(actionOrigin ? { actionOrigin } : {}),
      ...(linkText ? { linkText } : {}),
    });
  };

  const displayAdViewed = ({ displayAdPlacement }: DisplayAdViewedProps) => {
    tealium.view('display_ad_viewed', {
      ...getDataLayer(),
      displayAdPlacement,
    });
  };

  const linkClicked = ({
    href,
    linkContext,
    linkName,
    experimentFlightId,
    trackingCorrelationId,
  }: LinkPayload) => {
    const mappedEventName = mapEventName(linkName) || linkName;

    if (experimentFlightId) {
      updateDataLayer({
        experimentFlightId,
      });
    }

    tealium.link(mappedEventName, {
      ...getDataLayer(),
      ...mapLinkType({
        linkName: mappedEventName,
        href,
      }),
      ...(linkContext ? mapLinkContext(linkContext) : {}),
      trackingCorrelationId,
    });
  };

  const companyProfileWidgetImpression: Analytics['companyProfileWidgetImpression'] =
    () => {
      tealium.link('company_profile_widget_viewed', {
        ...getDataLayer(),
      });
    };

  const companyProfileWidgetAccordionLinkClicked: Analytics['companyProfileWidgetAccordionLinkClicked'] =
    (linkContext: LinkContext) => {
      const eventName = 'accordion_pressed';
      const { linkAction } = linkContext;

      tealium.link(eventName, {
        ...getDataLayer(),
        linkAction,
        linkName: linkAction === 'expand' ? 'Show more' : 'Show less',
      });
    };

  const companyProfileWidgetReviewsLinkClicked: Analytics['companyProfileWidgetReviewsLinkClicked'] =
    (linkContext: LinkContext) => {
      const eventName = 'company_reviews_pressed';
      const { linkPosition } = linkContext;

      tealium.link(eventName, {
        ...getDataLayer(),
        linkPosition,
      });
    };

  const companyProfileWidgetLinkClicked: Analytics['companyProfileWidgetLinkClicked'] =
    (linkContext: LinkContext) => {
      const eventName = 'company_widget_button_pressed';
      const { linkSection } = linkContext;

      tealium.link(eventName, {
        ...getDataLayer(),
        linkSection,
        linkName: 'More about this company',
      });
    };

  const companySuggestionLinkPressed: Analytics['companySuggestionLinkPressed'] =
    (isExactCompanyLink) => {
      const eventName = isExactCompanyLink
        ? 'exact_company_pressed'
        : 'broadened_company_pressed';

      updateDataLayer(
        {},
        {
          keepKeysForNextPrune: [
            'searchOrigin',
            'relatedSearchDisplayCount',
            'locationInput',
          ],
        },
      );

      tealium.link(eventName, {
        ...getDataLayer(),
        isExitLink: false,
        companySearchType: isExactCompanyLink ? 'exact' : 'broad',
      });
    };

  const navigationPressed = (linkContext: NavigationPressedProps) => {
    tealium.link('navigation_pressed', {
      ...getDataLayer(),
      ...mapLinkContext(linkContext),
    });
  };

  const experiments = (props: Experiment) => {
    updateDataLayer({ experiments: props });
  };

  const setExperiments: Analytics['setExperiments'] = ({
    experimentsEligible,
    hubbleExperiments,
  }) => {
    updateDataLayer({
      experimentsEligible,
      hubbleExperiments: {
        ...getDataLayer().hubbleExperiments,
        ...hubbleExperiments,
      },
    });
  };

  const hubbleExperiments = (props: HubbleExperiments) => {
    updateDataLayer({
      hubbleExperiments: {
        ...getDataLayer().hubbleExperiments,
        ...props,
      },
    });
  };

  const lmisImpression = ({
    experimentFlightId,
    lmisEntity,
    lmisResultType,
    lmisSnippet,
    trackingCorrelationId,
  }: LmisTypePayload) => {
    if (experimentFlightId) {
      updateDataLayer({
        experimentFlightId,
      });
    }

    tealium.link('lmis_viewed', {
      ...getDataLayer(),
      linkName: 'lmis_viewed',
      ...(lmisEntity && { lmisEntity }),
      ...(lmisResultType && { lmisResultType }),
      ...(lmisSnippet && { lmisSnippet }),
      trackingCorrelationId,
    });
  };

  const lmisClicked = ({
    experimentFlightId,
    lmisEntity,
    lmisResultType,
    lmisSnippet,
    trackingCorrelationId,
  }: LmisTypePayload) => {
    if (experimentFlightId) {
      updateDataLayer({
        experimentFlightId,
      });
    }

    tealium.link('lmis_pressed', {
      ...getDataLayer(),
      linkName: 'lmis_pressed',
      ...(lmisEntity && { lmisEntity }),
      ...(lmisResultType && { lmisResultType }),
      ...(lmisSnippet && { lmisSnippet }),
      trackingCorrelationId,
    });
  };

  const salaryBadgeClicked: Analytics['salaryBadgeClicked'] = () => {
    const eventName = 'job_salary_badge_details_pressed';
    tealium.link(eventName, {
      ...getDataLayer(),
      linkName: eventName,
    });
  };

  const sourcrImpression = () => {
    tealium.link('opportunity_viewed', {
      ...getDataLayer(),
      opportunityType: 'recruiter',
      linkName: 'opportunity_viewed',
    });
  };

  const sourcrClicked = () => {
    tealium.link('opportunity_pressed', {
      ...getDataLayer(),
      opportunityType: 'recruiter',
      linkName: 'opportunity_pressed',
    });
  };

  const getScriptTags = () => [
    getTealiumTag({
      account: 'seek',
      profile,
      environment: getEnv(environment),
    }),
  ];

  const shareLinkClicked = ({
    jobShareType,
    experimentFlightId,
  }: {
    jobShareType: ShareType;
    experimentFlightId: string;
  }) => {
    tealium.link('job_share_pressed', {
      ...getDataLayer(),
      jobShareType,
      experimentFlightId,
    });
  };
  const contactMatchClicked = (contactInteraction: ContactMatchPayload) => {
    if (contactInteraction.experimentFlightId) {
      updateDataLayer({
        experimentFlightId: contactInteraction.experimentFlightId,
      });
    }

    tealium.link('job_contact_pressed', {
      ...getDataLayer(),
      ...contactInteraction,
    });
  };

  const reportJobClicked = () => {
    tealium.link('report_job_pressed', {
      ...getDataLayer(),
      linkName: 'report_job_pressed',
    });
  };

  const relatedSearchPressed = ({
    linkContext: { linkPosition, linkText },
  }: RelatedSearchPressedProps) => {
    const linkName = 'related_search_pressed';
    tealium.link(linkName, {
      ...getDataLayer(),
      linkName,
      linkContext: {
        linkPosition,
        linkText,
      },
      isExitLink: false,
    });

    updateDataLayer(
      {
        relatedSearchPosition: linkPosition,
        searchOrigin: searchOrigins.RELATED_SEARCH,
      },
      { keepKeysForNextPrune: ['relatedSearchPosition', 'searchOrigin'] },
    );
  };

  const paginationPressed = () => {
    updateDataLayer(
      {},
      {
        keepKeysForNextPrune: [
          'relatedSearchDisplayCount',
          'relatedSearchPosition',
          'searchOrigin',
          'locationInput',
        ],
      },
    );
  };

  const branchBannerCTAPressed: Analytics['branchBannerCTAPressed'] = (
    branchTag,
  ) => {
    const linkName = 'app_banner_cta_pressed';
    tealium.link(linkName, {
      ...getDataLayer(),
      branchTag,
      linkName,
      isExitLink: false,
    });
  };

  const branchBannerDismissPressed: Analytics['branchBannerDismissPressed'] = (
    branchTag,
  ) => {
    const linkName = 'app_banner_dismiss_pressed';
    tealium.link(linkName, {
      ...getDataLayer(),
      branchTag,
      linkName,
    });
  };

  const branchBannerImpression: Analytics['branchBannerImpression'] = (
    branchTag,
  ) => {
    const linkName = 'app_banner_viewed';
    tealium.link('app_banner_viewed', {
      ...getDataLayer(),
      branchTag,
      linkName,
    });
  };

  const searchFormSubmitted: Analytics['searchFormSubmitted'] = () => {
    const { currentPage, sortedBy = 'KeywordRelevance' } = getDataLayer();
    updateDataLayer(
      {
        sortedBy,
        searchOrigin:
          currentPage === 'home'
            ? searchOrigins.JOB_SEARCH_HOME
            : searchOrigins.JOB_SEARCH_SERP,
      },
      { keepKeysForNextPrune: ['searchOrigin'] },
    );
  };

  const moreOptionsClicked: Analytics['moreOptionsClicked'] = () => {
    const linkName = 'more-options-clicked';

    tealium.link(linkName, {
      ...getDataLayer(),
      linkName,
    });
  };

  const searchBySuburbClicked: Analytics['searchBySuburbClicked'] = () => {
    const linkName = 'search-by-suburb-clicked';

    tealium.link(linkName, {
      ...getDataLayer(),
      linkName,
      isExitLink: false,
    });

    updateDataLayer(
      {
        searchOrigin: searchOrigins.FOOTER_SEARCH_SUBURB,
      },
      { keepKeysForNextPrune: ['searchOrigin'] },
    );
  };

  const saveSearchFormDisclaimerLinkClicked: Analytics['saveSearchFormDisclaimerLinkClicked'] =
    () => {
      const linkName = 'jobmail:privacy';

      tealium.link(linkName, {
        ...getDataLayer(),
        linkName,
        isExitLink: false,
      });
    };

  // Country Landing Page
  const landingPageCountrySelectionPressed: Analytics['landingPageCountrySelectionPressed'] =
    (args) => {
      const eventName = 'link_pressed';

      tealium.link(eventName, {
        ...getDataLayer(),
        ...args,
      });
    };

  // Candidate Onboarding banner
  const onboardingExperiencePressed: Analytics['onboardingExperiencePressed'] =
    () => {
      tealium.link('onboarding_experience_pressed', {
        ...getDataLayer(),
        linkAction: 'link',
        elementDesign: 'onboarding dialog',
      });
    };

  const onboardingExperienceDismissPressed: Analytics['onboardingExperienceDismissPressed'] =
    () => {
      tealium.link('onboarding_experience_pressed', {
        ...getDataLayer(),
        linkAction: 'close',
        elementDesign: 'onboarding dialog',
      });
    };

  const onboardingExperienceAlertPressed: Analytics['onboardingExperienceAlertPressed'] =
    () => {
      tealium.link('alert_pressed', {
        ...getDataLayer(),
        linkText: 'Onboarding alert - see what is new',
        alertShownType: 'alert',
        alertShownTone: 'promoted',
        alertShownPosition: 'content',
        alertShownMessage: `Finding the right job just got easier. See what is new on ${
          getDataLayer().brand ?? 'N/A'
        }`,
        alertShownMessageCode: 'what_is_new_on_brand',
      });
    };

  const onboardingExperienceAlertDimissPressed: Analytics['onboardingExperienceAlertDimissPressed'] =
    () => {
      tealium.link('alert_dismiss_pressed', {
        ...getDataLayer(),
        alertShownType: 'alert',
        alertShownTone: 'promoted',
        alertShownPosition: 'content',
        alertShownMessage: `Finding the right job just got easier. See what is new on ${
          getDataLayer().brand ?? 'SEEK'
        }`,
        alertShownMessageCode: 'what_is_new_on_brand',
      });
    };

  const homepageMarketingBannerClicked: Analytics['homepageMarketingBannerClicked'] =
    ({
      linkContext: { linkAction, linkText },
    }: HomepageMarketingBannerClickedProps) => {
      const eventName = 'banner_pressed';
      const bannerEntity = 'discover';
      const actionOrigin = 'candidate homepage footer';
      const currentPage = 'home';

      tealium.link(eventName, {
        ...getDataLayer(),
        linkAction,
        linkText,
        bannerEntity,
        actionOrigin,
        currentPage,
      });
    };

  const googleOneTapPressed: Analytics['googleOneTapPressed'] = ({
    actionOrigin,
  }) => {
    tealium.link('login_pressed', {
      ...getDataLayer(),
      identityProvider: 'google 1-tap',
      actionOrigin,
    });
  };

  /* POEA Start */

  const poeaCountryPickerPageLoaded = ({
    currentPage,
  }: {
    currentPage: string;
  }) => {
    const eventName = 'country_picker_displayed';
    tealium.view(eventName, {
      ...getDataLayer(),
      currentPage,
    });
  };

  const poeaLinksPressed: Analytics['poeaLinksPressed'] = (args) => {
    const eventName = 'link_pressed';
    tealium.link(eventName, {
      ...getDataLayer(),
      ...args,
    });
  };

  /* Skills Match Start */

  const skillsMatchRegisterPressed: Analytics['skillsMatchRegisterPressed'] = ({
    currentPageSection,
  }) => {
    tealium.link('register_pressed', {
      ...getDataLayer(),
      currentPageSection,
    });
  };

  const skillsMatchSignInPressed: Analytics['skillsMatchSignInPressed'] = ({
    currentPageSection,
  }) => {
    tealium.link('sign_in_pressed', {
      ...getDataLayer(),
      currentPageSection,
    });
  };

  const skillsMatchFeedbackPressed: Analytics['skillsMatchFeedbackPressed'] = ({
    linkAction,
  }) => {
    tealium.link('matched_skills_feedback_pressed', {
      ...getDataLayer(),
      linkAction,
    });
  };

  const skillsMatchProfileUpdated: Analytics['skillsMatchProfileUpdated'] = ({
    currentPageSection,
    updateType,
    actionOrigin,
    skillsAdded,
    skillsStartTotal,
    skillsFromSuggestedTotal,
    skillsCompleteTotal,
  }) => {
    tealium.link('profile_update_succeeded', {
      ...getDataLayer(),
      currentPageSection,
      updateType,
      actionOrigin,
      skillsAdded,
      skillsStartTotal,
      skillsFromSuggestedTotal,
      skillsCompleteTotal,
    });
  };

  const skillsMatchAlert: Analytics['skillsMatchAlert'] = ({
    currentPageSection,
    alertShownTone,
    alertShownType,
    alertShownMessage,
  }) => {
    tealium.link('alert_viewed', {
      ...getDataLayer(),
      currentPageSection,
      alertShownTone,
      alertShownType,
      alertShownMessage,
    });
  };

  const skillsMatchAccordionPressed: Analytics['skillsMatchAccordionPressed'] =
    ({ linkAction, linkSection }) => {
      tealium.link('accordion_pressed', {
        ...getDataLayer(),
        linkAction,
        linkSection,
      });
    };

  const skillsMatchLoadFailed: Analytics['skillsMatchLoadFailed'] = ({
    currentPageSection,
    isLoggedIn,
    matchedSkillsVisibilityStatus,
  }) => {
    tealium.link('skills_match_load_failed', {
      ...getDataLayer(),
      currentPageSection,
      isLoggedIn,
      matchedSkillsVisibilityStatus,
    });
  };

  /* Skills Match End */

  /* Homepage GenAI Start */
  const homepageGenAIProductName = 'Home Feed';

  const getHomePageGenAIDataLayer = () => {
    const {
      brand,
      currentPage,
      experiments: currentExperiments,
      experimentsEligible,
      experimentFlightId,
      hubbleExperiments: currentHubbleExperiments,
      isLoggedIn,
      loginId,
      profileGuid,
      siteCountry,
      siteLanguage,
      siteSection,
      solVisitorId,
      zone,
    } = getDataLayer();

    return {
      brand,
      currentPage,
      experiments: currentExperiments,
      experimentsEligible,
      experimentFlightId,
      hubbleExperiments: currentHubbleExperiments,
      isLoggedIn,
      loginId,
      profileGuid,
      siteCountry,
      siteLanguage,
      siteSection,
      solVisitorId,
      zone,
    };
  };

  const homepageGenAIChatCtaDisplayed: Analytics['homepageGenAIChatCtaDisplayed'] =
    ({ chatSessionId }) => {
      tealium.link('gen_ai_chat_cta_displayed', {
        ...getHomePageGenAIDataLayer(),
        chatSessionId,
        product: homepageGenAIProductName,
      });
    };

  const homepageGenAIChatStartPressed: Analytics['homepageGenAIChatStartPressed'] =
    ({ chatSessionId }) => {
      tealium.link('gen_ai_chat_start_pressed', {
        ...getHomePageGenAIDataLayer(),
        chatSessionId,
        product: homepageGenAIProductName,
      });
    };

  const homepageGenAIChatStarted: Analytics['homepageGenAIChatStartPressed'] =
    ({ chatSessionId }) => {
      tealium.link('gen_ai_chat_started', {
        ...getHomePageGenAIDataLayer(),
        chatSessionId,
        product: homepageGenAIProductName,
      });
    };

  const homepageGenAIChatOutputDisplayed: Analytics['homepageGenAIChatOutputDisplayed'] =
    (args) => {
      tealium.link('gen_ai_chat_output_displayed', {
        ...getHomePageGenAIDataLayer(),
        ...args,
        product: homepageGenAIProductName,
      });
    };

  const homepageGenAIChatFeedbackPressed: Analytics['homepageGenAIChatFeedbackPressed'] =
    (args) => {
      tealium.link('gen_ai_chat_feedback_pressed', {
        ...getHomePageGenAIDataLayer(),
        ...args,
        product: homepageGenAIProductName,
      });
    };

  const homepageGenAIInputPressed: Analytics['homepageGenAIInputPressed'] = (
    args,
  ) => {
    tealium.link('gen_ai_chat_input_pressed', {
      ...getHomePageGenAIDataLayer(),
      ...args,
      product: homepageGenAIProductName,
    });
  };

  const homepageGenAIInputSucceeded: Analytics['homepageGenAIInputSucceeded'] =
    (args) => {
      tealium.link('gen_ai_chat_input_succeeded', {
        ...getHomePageGenAIDataLayer(),
        ...args,
        product: homepageGenAIProductName,
      });
    };

  const homepageGenAIChatEnded: Analytics['homepageGenAIChatEnded'] = (
    args,
  ) => {
    tealium.link('gen_ai_chat_ended', {
      ...getHomePageGenAIDataLayer(),
      chatSessionId: args.chatSessionId,
      chatOutputLastSequence: args.lastOutputSequence,
      chatOutputType: args.outputType,
      product: homepageGenAIProductName,
    });
  };

  const homepageGenAIRecommendedJobsRefreshed: Analytics['homepageGenAIRecommendedJobsRefreshed'] =
    (args) => {
      tealium.link('recommended_jobs_refreshed', {
        ...getHomePageGenAIDataLayer(),
        ...args,
        product: 'Home Recommended Jobs',
        actionOrigin: 'GenAI Chat in Feed',
      });
    };

  const homepageGenAIChatActionPressed: Analytics['homepageGenAIChatActionPressed'] =
    (args) => {
      tealium.link('gen_ai_chat_action_pressed', {
        ...getHomePageGenAIDataLayer(),
        elementAction: args.action,
        elementPosition: args.buttonPosition,
        product: homepageGenAIProductName,
      });
    };

  /* Homepage GenAI End */

  return {
    // setup
    getScriptTags,

    // data layers
    getDataLayer,

    // page loads
    globalLandingPageLoaded,
    homePageLoaded,
    jobDetailsPageLoaded,
    searchResultsPageLoaded,
    feedbackSurveyViewed,
    poeaCountryPickerPageLoaded,

    // updates
    experiments,
    setExperiments,
    hubbleExperiments,
    jobDetailsFetched,
    keywordChanged,
    countryChanged,
    locationChanged,
    savedJobCreated,
    savedJobRemoved,
    searchPerformed,
    sortModeChanged,
    userDetailsUpdated,
    // impressions
    companyProfileWidgetImpression,
    createUnregisteredSaveSearchError,
    lmisImpression,
    nudgeDismissed,
    nudgePressed,
    nudgeViewed,
    loggedOutRecsImpression,
    recentSearchesImpression,
    sourcrImpression,

    // clicks
    contactMatchClicked,
    createSaveSearchClicked,
    createSaveSearchError,
    createUnregisteredSaveSearchClicked,
    lastSearchClicked,
    linkClicked,
    jobApplyClicked,
    jobCardAccordionLinkClicked,
    companyProfileWidgetAccordionLinkClicked,
    companyProfileWidgetReviewsLinkClicked,
    companyProfileWidgetLinkClicked,
    companySuggestionLinkPressed,
    lmisClicked,
    moreOptionsClicked,
    navigationPressed,
    paginationPressed,
    recentSearchClicked,
    registerPressed,
    relatedSearchPressed,
    reportJobClicked,
    salaryBadgeClicked,
    saveJobClicked,
    saveSearchFormDisclaimerLinkClicked,
    searchFormSubmitted,
    searchBySuburbClicked,
    searchHelpPressed,
    shareLinkClicked,
    signInPressed,
    sourcrClicked,
    verifiedHirerBadgeClicked,
    googleOneTapPressed,
    poeaLinksPressed,
    feedbackSurveyPressed,

    // Branch Banner
    branchBannerImpression,
    branchBannerCTAPressed,
    branchBannerDismissPressed,

    // Country Landing Page
    landingPageCountrySelectionPressed,

    // Candidate Onboarding Banner
    onboardingExperienceAlertPressed,
    onboardingExperienceAlertDimissPressed,
    onboardingExperiencePressed,
    onboardingExperienceDismissPressed,

    // Homepage Banners
    homepageMarketingBannerClicked,

    // Display ads
    displayAdViewed,

    // Skills Match
    skillsMatchRegisterPressed,
    skillsMatchSignInPressed,
    skillsMatchFeedbackPressed,
    skillsMatchProfileUpdated,
    skillsMatchAlert,
    skillsMatchAccordionPressed,
    skillsMatchLoadFailed,

    // Homepage GenAI
    homepageGenAIChatCtaDisplayed,
    homepageGenAIChatStartPressed,
    homepageGenAIChatStarted,
    homepageGenAIChatOutputDisplayed,
    homepageGenAIChatFeedbackPressed,
    homepageGenAIInputSucceeded,
    homepageGenAIInputPressed,
    homepageGenAIChatEnded,
    homepageGenAIRecommendedJobsRefreshed,
    homepageGenAIChatActionPressed,
  };
};

export {
  createAnalytics,
  hashToUserQueryId,
  mapJobTagsToBadges,
  type PageName,
  type LinkPayload as LinkClickedProps,
  type NavigationPressedProps,
  type Analytics,
  type JobActionOrigin,
  type JobViewType,
};
